/**
 * The onboarding route lock (Vera onboarding spec 2026-10-12, §5.2 and D9):
 * a non-admin user whose brand is ONBOARDING reaches /onboarding/*, and
 * /catalog/* only once product onboarding is unlocked. Everything else sends
 * them to /onboarding. Admins and LIVE brands are untouched. The unlock itself
 * comes from users.me (the API's onboardingAccess); nothing here re-derives it.
 *
 * No imports: the API's Jest loads this file for the route-lock claim proofs
 * (plan Task 7, apps/api/src/onboarding/route-lock.web.claims.spec.ts).
 */
export const ONBOARDING_HOME = '/onboarding';
/** = CATALOG_BASE (pages/catalog/catalogPaths.ts:11); onboardingRedirect.test.ts pins the two together. */
export const CATALOG_PATH = '/catalog';
/** Where product onboarding starts (CatalogConnect). */
export const PRODUCT_ONBOARDING_HOME = `${CATALOG_PATH}/connect`;
/**
 * Catalog views that read live catalog and marketplace data (browse:
 * portal.catalogBrowse; marketplaces: marketplace.*). An onboarding brand has
 * none, so they stay closed until it is LIVE, even once product onboarding
 * opens (Ari, 2026-10-13). The API refuses their calls too.
 */
export const LIVE_ONLY_CATALOG_PATHS: readonly string[] = [CATALOG_PATH, `${CATALOG_PATH}/marketplaces`];

const under = (pathname: string, base: string): boolean => pathname === base || pathname.startsWith(`${base}/`);

const liveOnly = (pathname: string): boolean => {
  const path = pathname.replace(/\/+$/, '') || '/';
  return path === LIVE_ONLY_CATALOG_PATHS[0] || under(path, LIVE_ONLY_CATALOG_PATHS[1]);
};

export interface LockUser {
  role: string;
  /** The user's default brand (users.me). With a brand, users.me always carries `onboarding`. */
  brandId?: number | null;
  onboarding?: { stage: 'ONBOARDING' | 'LIVE'; access: { productOnboarding: boolean } } | null;
}

const STAFF_ROLES: ReadonlySet<string> = new Set(['ADMIN', 'SUPER_ADMIN']);

export function isOnboardingLocked(user: LockUser | null | undefined): boolean {
  return !!user && !STAFF_ROLES.has(user.role) && user.onboarding?.stage === 'ONBOARDING';
}

export function onboardingRedirect(pathname: string, user: LockUser | null | undefined): string | null {
  if (!user || !isOnboardingLocked(user)) return null;
  if (under(pathname, ONBOARDING_HOME)) return null;
  if (liveOnly(pathname)) return user.onboarding?.access.productOnboarding ? PRODUCT_ONBOARDING_HOME : ONBOARDING_HOME;
  if (user.onboarding?.access.productOnboarding && under(pathname, CATALOG_PATH)) return null;
  return ONBOARDING_HOME;
}

/**
 * Fail closed: a non-admin user with a brand whose onboarding stage did not
 * resolve (users.me without `onboarding`) may be onboarding, so they get an
 * error state, never the portal. Admins and brandless users are unaffected.
 */
export function onboardingStageUnknown(user: LockUser | null | undefined): boolean {
  return !!user && user.brandId != null && user.onboarding == null && !STAFF_ROLES.has(user.role);
}
